import React, { useEffect, useState } from "react";

/**
 * Iter 31.206 — Elapsed / remaining time readout for the radio + media
 * player surfaces. Hooks a live <audio> element via `timeupdate` and
 * `loadedmetadata`. Renders as `M:SS / -M:SS` (remaining is negative
 * so it reads as a count-down, like a car stereo).
 *
 * Defensive: if duration is NaN/Infinity (live stream, mid-load), only
 * the elapsed half renders. If audioElement is null, renders an em-dash.
 */
function fmt(secs) {
    if (!Number.isFinite(secs) || secs < 0) return null;
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function TrackTime({ audioElement, className = "", testId = "track-time" }) {
    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(NaN);

    useEffect(() => {
        const el = audioElement;
        if (!el) return undefined;
        const onTime = () => setElapsed(el.currentTime || 0);
        const onMeta = () => setDuration(el.duration);
        const onEnd = () => setElapsed(el.duration || 0);
        // Prime on mount in case metadata is already loaded.
        if (el.readyState >= 1) {
            setDuration(el.duration);
            setElapsed(el.currentTime || 0);
        }
        el.addEventListener("timeupdate", onTime);
        el.addEventListener("loadedmetadata", onMeta);
        el.addEventListener("durationchange", onMeta);
        el.addEventListener("ended", onEnd);
        return () => {
            el.removeEventListener("timeupdate", onTime);
            el.removeEventListener("loadedmetadata", onMeta);
            el.removeEventListener("durationchange", onMeta);
            el.removeEventListener("ended", onEnd);
        };
    }, [audioElement]);

    if (!audioElement) {
        return (
            <span className={`tabular-nums ${className}`} data-testid={testId}>
                —
            </span>
        );
    }
    const elapsedStr = fmt(elapsed) || "0:00";
    const remaining = Number.isFinite(duration) ? duration - elapsed : NaN;
    const remainingStr = fmt(remaining);
    return (
        <span className={`tabular-nums ${className}`} data-testid={testId}>
            <span data-testid={`${testId}-elapsed`}>{elapsedStr}</span>
            {remainingStr ? (
                <>
                    <span className="opacity-50 mx-1">/</span>
                    <span data-testid={`${testId}-remaining`}>-{remainingStr}</span>
                </>
            ) : null}
        </span>
    );
}
